import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from 'antd';

import { useBranch, useUpdateBranchPrices } from '../../hooks';
import { createLink, removeObjectProperties, searchFn } from '../../utils';
import { ROUTE_URLS } from '../../routes';
import {
  AmountInput,
  Card,
  EmptyState,
  FormActions,
  FormSkeleton,
  PageHeader,
  SearchInput,
} from '../../components';
import { useI18n } from '../../i18n';

export const EditBranchPrices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: branch, isLoading: isDataLoading, isError } = useBranch(id);
  const { mutateAsync: editBranchPrices, isLoading } = useUpdateBranchPrices(id);

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (isError) {
      navigate(ROUTE_URLS.BRANCHES);
    }
  }, [isError, navigate]);

  const defaultValues = useMemo(() => {
    const values = {};
    branch?.products?.forEach(product => {
      values[product.id] = product.sell_price ?? 0;
    });
    return values;
  }, [branch]);

  const filteredProducts = useMemo(() => {
    if (searchText) {
      return searchFn(branch?.products, searchText);
    }
    return branch?.products ?? [];
  }, [branch, searchText]);

  const onFinish = values => {
    const readyValues = {
      id,
      product_ids: { ...removeObjectProperties(values, ['productName']) },
    };
    editBranchPrices(readyValues);
  };

  const backTo = createLink(ROUTE_URLS.BRANCH_PRICES, id);

  return (
    <div className='wm-form-page'>
      <PageHeader
        backTo={backTo}
        backLabel={t('branches.prices')}
        title={branch ? t('branches.editPricesTitle', { branch: branch.name }) : t('branches.editPrices')}
        subtitle={t('branches.editPricesSubtitle')}
      />
      {isDataLoading ? (
        <Card>
          <FormSkeleton fields={5} />
        </Card>
      ) : (
        <Form name='fields' layout='vertical' onFinish={onFinish} initialValues={defaultValues}>
          <Card
            title={t('createReport.products')}
            extra={
              <SearchInput
                value={searchText}
                onChange={setSearchText}
                placeholder={t('products.searchPlaceholder')}
              />
            }
          >
            {filteredProducts.length === 0 ? (
              <EmptyState variant='search' compact />
            ) : (
              <div className='wm-qty-list'>
                {filteredProducts.map(product => (
                  <div className='wm-qty-row' key={product.id}>
                    <div className='wm-qty-row__name'>{product.name}</div>
                    <Form.Item
                      name={product.id}
                      rules={[{ required: true, message: t('validation.required', { field: t('branches.sellPrice') }) }]}
                    >
                      <AmountInput placeholder={t('branches.pricePlaceholder')} aria-label={product.name} />
                    </Form.Item>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <FormActions cancelTo={backTo} submitting={isLoading} />
        </Form>
      )}
    </div>
  );
};
